import { GENRE_LIBRARY } from '../data/GenreLibrary.js';
import { SCALE_INTERVALS, CHORD_FORMULAS, PROGRESSION_TEMPLATES, FUNCTIONAL_SUBSTITUTIONS, GROOVE_TEMPLATES, RHYTHM_PATTERNS, NOTES, normalizeKey } from '../data/theoryConstants.js';
import { getRandomPattern } from '../data/RhythmLibrary.js';
import { MOTIF_PATTERNS } from '../data/MotifLibrary.js';
import { allocateRoles, adaptEvents, ABSTRACT_ROLES } from './RoleAllocator.js';
import { getMelodyStrategy, transformPattern, decorateMelody, generateSwarm, augmentRhythm, generateOstinato, generatePhasingPattern, generateAdditiveMotif, generateArpeggio, applyGrooveLocking } from './MelodyStrategies.js';
import { getBassStrategy } from './BassStrategies.js';
import { applyVoiceLeading, applyGenreVoicing, applyLegatoGlue, validateVoiceLeading, shouldAllowLegatoOverlap } from './VoiceLeading.js';
import { applyOrchestralVoicing, applyHornFifths, applyPedalPoint } from './OrchestralLogic/VoicingEngine.js';
import { calculateTension, applyTensionToDynamics } from './OrchestralLogic/TensionEngine.js';
import { generateCounterLine } from './OrchestralLogic/CounterpointEngine.js';
import { getPercussionNote, generateTimpaniRoll, generateCymbalCrash, generateActionBattery } from './OrchestralLogic/PercussionLogic.js';
import { applySATBVoiceLeading } from './OrchestralLogic/SATBEngine.js';
import { MotifManager } from './OrchestralLogic/MotifManager.js';
import { applyPhrasing, applyKeyswitches } from './OrchestralLogic/ArticulationEngine.js';
import { suggestSectionKeys } from './OrchestralLogic/ModulationEngine.js';
import { generateLockedGroove } from './OrchestralLogic/CompingEngine.js';
import { generateDrumFill, generateTransition } from './OrchestralLogic/TransitionEngine.js';
import { generateWoodwindRun, generateMotifComping, generateCluster } from './OrchestralLogic/TextureEngine.js';
import { getDrumStrategy } from './DrumStrategies.js';
import { generateAleatoricCloud } from './OrchestralLogic/AleatoricEngine.js';
import { generateTempoMap } from './OrchestralLogic/TempoEngine.js';
import { Random } from './Random.js';
import { getActiveRoles, getSectionIntensityCurve } from './OrchestralLogic/Orchestrator.js';
import { enforceHarmonicContext } from './HarmonyManager.js';
import { getSeededConfig } from './SeededConfig.js';
import { analyzeMelody } from './Analysis/CounterpointAnalysis.js';
import { generateTensionCurve } from './NarrativeEngine.js';
import { applyPhraseShaping, applyBreathConstraints } from './Humanizer.js';
import { applyStrum, applyGuitarVoicing } from './StrummingEngine.js';
import { applyGroove } from './GrooveEngine.js';
import { APP_VERSION } from '../constants.js';
import { GenerationPipeline } from './GenerationPipeline.js';
import { createRegistry, addToRegistry } from './RegistryUtils.js';
import { MELODY_WEIGHTS, BASS_WEIGHTS, BASS_CONFIG } from '../data/StrategyConfig.js';

// Density Thresholds for Harmony generation
// Fix: "Density Trace" - Lowered thresholds to prevent silence at low/moderate user inputs (which map to very low vectors).
const TACIT_DENSITY_THRESHOLD = 0.001; // Was 0.01. Strict Non-Zero Policy.
const LEGATO_DENSITY_THRESHOLD = 0.2; // Was 0.6. Switch to rhythmic comping earlier.

export class GenerationProfile {
    constructor(options = {}) {
        this.harmonySource = options.harmonySource || 'epic_cinematic';
        this.rhythmSource = options.rhythmSource || 'basic_rock';
        this.voicingSource = options.voicingSource || 'epic_cinematic';

        // Complexity Matrix (Granular Control)
        // Fallback to globalComplexity if specific vectors are not provided
        const baseComplexity = (options.globalComplexity || 50) / 100; // Normalize 0-1

        this.rhythmicDensity = options.rhythmicDensity !== undefined ? options.rhythmicDensity : baseComplexity;
        this.harmonicFreedom = options.harmonicFreedom !== undefined ? options.harmonicFreedom : baseComplexity;
        this.melodicRange = options.melodicRange !== undefined ? options.melodicRange : baseComplexity;
        this.timbralMotion = options.timbralMotion !== undefined ? options.timbralMotion : baseComplexity;

        this.tensionCurve = options.tensionCurve || 'Standard';
        this.humanize = options.humanize || 50;
        this.humanizeTempo = options.humanizeTempo || false;
        this.userScale = options.userScale || 'Auto';
        this.seed = options.seed || Math.floor(Math.random() * 999999);
    }
}

export function getEuclideanPattern(k, n) {
    let pattern = new Array(n).fill(0);
    if (k === 0) return pattern;
    if (k >= n) return pattern.fill(1);

    let bucket = 0;
    for (let i = 0; i < n; i++) {
        bucket += k;
        if (bucket >= n) {
            pattern[i] = 1;
            bucket -= n;
        }
    }
    if(pattern[0] !== 1) {
        const firstHit = pattern.indexOf(1);
        if(firstHit > 0) {
            pattern = [...pattern.slice(firstHit), ...pattern.slice(0, firstHit)];
        }
    }
    return pattern;
}

export class MusicEngine {
    constructor() {
        this.version = APP_VERSION;
        this.library = GENRE_LIBRARY;
        this.noteListeners = new Set();
        this.motifManager = null;
        this.random = new Random();
        this.pipeline = new GenerationPipeline(this);
    }

    registerNoteListener(cb) {
        this.noteListeners.add(cb);
        return () => this.noteListeners.delete(cb);
    }

    notifyNote(instrumentId, note) {
        this.noteListeners.forEach(cb => cb(instrumentId, note));
    }

    cleanOverlaps(events, context = {}) {
        const byInst = {};
        if (!Array.isArray(events)) return [];
        events.forEach(e => {
            const key = e.instrumentId || e.role;
            if(!byInst[key]) byInst[key] = [];
            byInst[key].push(e);
        });

        const cleaned = [];
        Object.values(byInst).forEach(instEvents => {
            instEvents.sort((a, b) => a.s - b.s);

            const uniqueEvents = [];
            instEvents.forEach(ev => {
                if (uniqueEvents.length === 0 || Math.abs(ev.s - uniqueEvents[uniqueEvents.length - 1].s) > 0.001) {
                    uniqueEvents.push(ev);
                } else {
                    const last = uniqueEvents[uniqueEvents.length - 1];
                    if ((ev.vel || 0) > (last.vel || 0)) {
                        uniqueEvents[uniqueEvents.length - 1] = ev;
                    }
                }
            });

            for (let i = 0; i < uniqueEvents.length - 1; i++) {
                const curr = uniqueEvents[i];
                const next = uniqueEvents[i+1];

                // Task 2: "Polyphony Gate" (Cloud, Ring, Glue)
                // Allow overlaps for specific behaviors

                // 1. Polyphonic / Ring (Cloud, Keys, Guitar, etc)
                const isRingOrCloud = curr.role === 'harmony_pad' || curr.role === 'harmony_color' || curr.role === 'aleatoric_pad' ||
                                      (curr.instrumentId && (
                                          curr.instrumentId.includes('piano') ||
                                          curr.instrumentId.includes('pad') ||
                                          curr.instrumentId.includes('guitar') ||
                                          curr.instrumentId.includes('harp') ||
                                          curr.instrumentId.includes('mallet') ||
                                          curr.instrumentId.includes('bell')
                                      ));

                // 2. Legato Glue (Melody/Strings/Winds wanting overlap)
                // Phase 1 Audit: Dynamic Legato Logic via VoiceLeading
                // We resolve the actual instrument object if possible to check capabilities
                let instrument = null;
                if (context.instruments && curr.instrumentId) {
                    instrument = context.instruments.find(i => i.id === curr.instrumentId);
                }

                // If 'shouldAllowLegatoOverlap' returns true, we PERMIT overlap (do not truncate).
                // Note: The function checks articulation, interval, and capability.
                const allowDynamicLegato = shouldAllowLegatoOverlap(curr, next, instrument);

                // Fix: "Infinite Melody" - Cap melody sustain at 4 bars even if legato
                const isTooLong = (curr.len > 4.0);
                const isIntentionalOverlap = (isRingOrCloud || allowDynamicLegato) && !isTooLong;

                if (!isIntentionalOverlap && next.s > curr.s && curr.s + curr.len > next.s) {
                    // Truncate to ensure NoteOff before next NoteOn
                    // Fix: Strict Gap Enforcement to prevent Negative Durations
                    // available space = distance between attacks
                    const available = next.s - curr.s;
                    // Desired gap = 0.01 (approx 20ms at 120bpm)
                    const gap = 0.01;

                    let newLen = available - gap;

                    // If available space is tiny (flam/roll), allow minimal duration but do NOT overlap
                    if (newLen <= 0) {
                        // Force minimal duration, even if it slightly overlaps (better than negative)
                        // But ideally, we should drop the note if it has no space.
                        // Let's enforce a hard floor of 0.01, which might overlap by 0.01 max.
                        newLen = 0.01;
                    }

                    curr.len = newLen;
                }
                cleaned.push(curr);
            }
            // Add last event without truncation (unless it exceeds absolute max, handled elsewhere)
            if (uniqueEvents.length > 0) cleaned.push(uniqueEvents[uniqueEvents.length - 1]);
        });

        return cleaned.sort((a, b) => a.s - b.s);
    }

    filterRepetition(events, scale) {
        const seen = new Set();
        return events.filter(e => {
            const t = Math.round(e.s * 100);
            const pitch = e.d !== undefined ? e.d : 'x';
            const src = e.instrumentId || e.role || 'unknown';
            const key = `${t}:${pitch}:${src}`;

            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    applySectionDynamics(events, sections, timeSig = 4, traits = null) {
        return events.filter(e => {
            let currentTime = 0;
            let currentSection = null;
            for(let s of sections) {
                const len = (s.type === 'easter_egg') ? 4 : s.bars;
                if (e.s >= currentTime && e.s < currentTime + (len * 4)) {
                     currentSection = s;
                     break;
                }
                currentTime += (len * 4);
            }

            if (!currentSection) return true;
            e.section = currentSection.type;

            const type = currentSection.type.toLowerCase();
            const role = e.role || '';
            const isHighPerc = role === ABSTRACT_ROLES.PERC_HIGH;
            const isBrass = e.instrumentId && (e.instrumentId.includes('trumpet') || e.instrumentId.includes('horn') || e.instrumentId.includes('trombone'));

            // Handle 'terraced' dynamics (Baroque): Flat levels per section
            if (traits && traits.velocityStrategy === 'terraced') {
                if (type.includes('chorus') || type.includes('climax')) {
                    e.vel = 1.0; // Fixed Fortissimo
                } else if (type.includes('intro') || type.includes('verse')) {
                    e.vel = 0.40; // Fixed Piano (Wider gap)
                } else {
                    e.vel = 0.70; // Mezzo-Forte
                }

                // Boost High-Frequency Instruments (Voice Distribution Hack)
                if (e.d > 70) e.vel = Math.min(1.0, e.vel * 1.2);

                // Small jitter to prevent machine-gun effect, but keep it tight for 'stepwise' scoring
                e.vel += ((Math.random() - 0.5) * 0.05);
                return true;
            }

            if (type.includes('chorus') || type.includes('climax') || type.includes('drop')) {
                 const multiplier = (traits && traits.velocityStrategy === 'dynamic_swell') ? 1.25 : 1.1;
                 e.vel = Math.min(1.0, (e.vel || 0.8) * multiplier);
                 return true;
            }

            if (type.includes('intro') || type.includes('verse') || type.includes('break')) {
                if (isBrass && type !== 'build') return false;
                if (isHighPerc && Math.random() > 0.5) return false;
                // Fix: Widen Dynamic Range for Romantic (dynamic_swell) by lowering the floor (0.7 -> 0.5)
                const multiplier = (traits && traits.velocityStrategy === 'dynamic_swell') ? 0.5 : 0.9;
                e.vel = (e.vel || 0.8) * multiplier;

                // Fix: Tame High-End for Epic Cinematic in Intro/Verse to allow buildup
                if (traits && traits.name === 'Epic Cinematic' && (isHighPerc || e.d > 84)) {
                    e.vel *= 0.8;
                }
            }

            return true;
        });
    }

    getGenreTraits(ensembleKey, subGenreKey) {
        if (!this.library) return null;
        let traits = null;
        let defaults = {};

        if (this.library[ensembleKey]) {
            defaults = { ...this.library[ensembleKey].defaults };
            if (this.library[ensembleKey].subGenres[subGenreKey]) {
                traits = { ...this.library[ensembleKey].subGenres[subGenreKey] };
                defaults = { ...defaults, ...traits.defaults };
            } else {
                // Fallback Logic: Use first subgenre but STRIP specific constraints
                // Fix: "Kitchen Sink Injection" - Prevent forced roles from Epic Cinematic leaking into unknown genres.
                const keys = Object.keys(this.library[ensembleKey].subGenres);
                if (keys.length > 0) {
                     // Clone the default
                     const fallback = this.library[ensembleKey].subGenres[keys[0]];
                     traits = { ...fallback };

                     // Sanitize specific constraints
                     delete traits.forcedRoles;
                     delete traits.melodicRules;
                     delete traits.roleBehaviors;

                     defaults = { ...defaults, ...traits.defaults };
                }
            }
        }

        if (!traits) return null;

        const merged = { ...traits, defaults };

        if (!merged.velocityStrategy) {
            const rString = (merged.rhythm || '').toLowerCase();
            if (rString.includes('driving') || rString.includes('machine') || rString.includes('fast')) {
                merged.velocityStrategy = 'fixed_max';
            } else if (rString.includes('rubato') || rString.includes('fluid')) {
                merged.velocityStrategy = 'dynamic_swell';
            } else {
                merged.velocityStrategy = 'groove';
            }
        }

        // Timbral Motion Override: Disable 'fixed_max' if high motion requested
        // This ensures the Motion Slider actually works for driving genres.
        // We can't access profile vectors here easily (pre-seed).
        // BUT GenerationPipeline calls getGenreTraits *after* initialization if re-rolling.
        // For initial load, we might need to handle this in `applySectionDynamics`.

        if (!merged.melodyStrategy) {
             const name = (merged.name || '').toLowerCase();
             if (name.includes('rock') || name.includes('metal')) merged.melodyStrategy = 'riff';
             else if (name.includes('minimal')) merged.melodyStrategy = 'ostinato';
             else merged.melodyStrategy = 'lyrical';
        }

        // Fuzzy Selection for Melody Strategy (if array)
        if (Array.isArray(merged.melodyStrategy)) {
            const opts = merged.melodyStrategy;
            // Use simple Math.random() here as this is trait resolution (pre-seed)
            // or pass context if available?
            // Traits are resolved per-call usually.
            // Ideally we'd use 'this.random' but getGenreTraits might be static-like.
            // Let's settle on one index based on nothing? No, random.
            const idx = Math.floor(Math.random() * opts.length);
            merged.melodyStrategy = opts[idx];
        }

        if (merged.syncopationLevel === undefined) {
            merged.syncopationLevel = 50;
        }

        if (!merged.defaults.scale) {
             const mood = (merged.logicTags || []).join(' ');
             if (mood.includes('sad') || mood.includes('dark')) merged.defaults.scale = 'minor';
             else merged.defaults.scale = 'major';
        }

        if (merged.name === 'Noir') {
            if (!merged.defaults.scale) merged.defaults.scale = 'melodic_minor';
            if (!merged.rhythmPattern) merged.rhythmPattern = 'jazz_brushes';
        }

        if (merged.name === 'Western') {
            if (!merged.defaults.scale) merged.defaults.scale = 'pentatonic_major';
            if (!merged.rhythmPattern) merged.rhythmPattern = 'western_gallop';
        }

        return merged;
    }

    updateContext(context) {
        if (this.motifManager) {
            this.motifManager.reset(context.random);
        }
        if (context.random) {
            this.random = context.random;
        }
        console.log(`[MusicEngine] Context Updated. New Seed: ${context.config ? context.config.seed : 'unknown'}`);
    }

    getEffectiveScale(userScale, harmonyTraits) {
        if (userScale && userScale !== 'Auto') {
            return userScale;
        }
        if (harmonyTraits) {
            if (harmonyTraits.logicTags) {
                const scaleTag = harmonyTraits.logicTags.find(t => t.startsWith('scale('));
                if (scaleTag) {
                    return scaleTag.match(/scale\(([^)]+)\)/)[1];
                }
            }
            const h = harmonyTraits.harmony.toLowerCase();
            if (h.includes('major')) return 'major';
            if (h.includes('minor')) return 'minor';
            if (h.includes('dorian')) return 'dorian';
            if (h.includes('phrygian')) return 'phrygian';
            if (h.includes('lydian')) return 'lydian';
            if (h.includes('mixolydian')) return 'mixolydian';
            if (h.includes('locrian')) return 'locrian';
        }
        return 'major';
    }

    generate(profile, context) {
        if (!(profile instanceof GenerationProfile)) {
            profile = new GenerationProfile(profile);
        }

        this.random.setSeed(profile.seed);
        context.random = this.random;
        context.cache = {};

        return this.pipeline.execute(profile, context);
    }

    getScale(root, type) {
        const intervals = SCALE_INTERVALS[type] || SCALE_INTERVALS.major;
        const normalizedRoot = normalizeKey(root);
        const rootIdx = NOTES.indexOf(normalizedRoot);
        if (rootIdx === -1) console.warn(`[MusicEngine] Unknown key: ${root} -> ${normalizedRoot}`);
        return intervals.map(i => (rootIdx + i) % 12);
    }

    getNearestChordTone(chord, prevPitch) {
        if (!chord) return prevPitch;
        const notes = chord.notes || [];
        if (notes.length === 0) return prevPitch;

        let best = notes[0];
        let minDist = Infinity;

        notes.forEach(n => {
            const dist = Math.abs(n - prevPitch);
            if (dist < minDist) {
                minDist = dist;
                best = n;
            }
        });
        return best;
    }

    applyCadentialLogic(progression, context) {
        if (!progression || progression.length < 2) return progression;

        const config = context.config || getSeededConfig('default', 12345);
        const cadenceChance = config.harmony ? config.harmony.cadenceChance : 0.8;

        for (let i = 0; i < progression.length; i++) {
            const current = progression[i];
            const next = progression[i+1];
            if (!current) continue;

            if (next && next.section !== current.section) {
                 if (next.key && current.key && next.key !== current.key) {
                      const targetRootStr = normalizeKey(next.key);
                      const targetRootPC = NOTES.indexOf(targetRootStr);
                      if (targetRootPC !== -1) {
                           const dominantPC = (targetRootPC + 7) % 12;
                           current.rootPitchClass = dominantPC;
                           current.quality = 'major';
                           current.type = 'dom7';
                           current.degree = 99;
                           current.isPivot = true;
                           current.analysis = `V7/${next.key}`;
                      }
                 }
                 else {
                      if (next.degree === 0) {
                           if (![4, 3, 6].includes(current.degree)) {
                                const currentScale = this.getScale(current.key, current.scale);
                                current.degree = 4;
                                current.rootPitchClass = currentScale[4];
                                current.quality = 'major';
                                current.type = 'dom7';
                                current.analysis = 'V7';
                           }
                      }
                 }
                 continue;
            }

            const chordEndBar = current.barIndex + (current.duration / (current.timeSig || 4));
            const endsPhrase = (Math.abs(chordEndBar % 4) < 0.01) && (chordEndBar > 0);

            if (endsPhrase) {
                 if (current.isPivot) continue;

                 if (this.random.next() < cadenceChance) {
                      const phraseIndex = Math.floor((chordEndBar - 0.1) / 4);
                      const isQuestion = (phraseIndex % 2 === 0);

                      const currentScale = this.getScale(current.key, current.scale);

                      if (isQuestion && next) {
                           current.degree = 4;
                           current.rootPitchClass = currentScale[4];
                           current.quality = 'major';
                           current.type = 'triad';
                           current.analysis = 'HC (V)';
                      } else {
                           if (!next) {
                               current.degree = 0;
                               current.rootPitchClass = currentScale[0];
                               current.quality = (current.scale === 'minor') ? 'minor' : 'major';
                               if (current.scale === 'minor' && this.random.bool(0.3)) current.quality = 'major';
                               current.type = 'triad';
                               current.analysis = 'Final (I)';
                           } else {
                               current.degree = 4;
                               current.rootPitchClass = currentScale[4];
                               current.quality = 'major';
                               current.type = 'dom7';
                               current.analysis = 'PAC Prep (V7)';
                           }
                      }
                 }
            }
        }
        return progression;
    }

    getChordProgression(traits, context, profile) {
        if (!traits) return [];

        const random = this.random;

        const rootKey = context.rootKey || 'C';
        const scaleType = context.scaleType || 'major';
        const hString = (traits.harmony || '').toLowerCase();
        const rString = (traits.rhythm || '').toLowerCase();

        const scale = this.getScale(rootKey, scaleType);

        let chordTypePreference = traits.chordComplexity || 'triad';

        // --- CONNECT HARMONIC FREEDOM ---
        const freedom = (context.config && context.config.vectors) ? context.config.vectors.harmonicComplexity : 0.5;

        if (!traits.chordComplexity) {
            // Override with Freedom vector
            if (freedom > 0.7) chordTypePreference = 'extended';
            else if (hString.includes('7th') || hString.includes('9th') || hString.includes('jazz')) chordTypePreference = 'extended';
            else if (hString.includes('power') || hString.includes('metal')) chordTypePreference = 'power';
        }

        const progressionConfig = context.config ? context.config.progression : null;

        let templateKey = 'pop_standard';
        if (freedom > 0.8) templateKey = 'jazz_standard'; // Force Jazz on high freedom
        else if (chordTypePreference === 'extended') templateKey = 'jazz_standard';
        if (scaleType === 'minor' && chordTypePreference === 'extended') templateKey = 'jazz_minor';
        if (hString.includes('epic') || hString.includes('cinematic')) templateKey = 'epic_heroic';
        if (hString.includes('dark') || hString.includes('tension')) templateKey = (progressionConfig ? progressionConfig.darkTension.template : 'dark_tension');
        if (hString.includes('punk') || hString.includes('rock')) templateKey = 'punk';

        if (hString.includes('medieval')) templateKey = 'medieval_modal';
        if (hString.includes('christmas')) templateKey = 'christmas_classic';
        if (traits.name === 'Western') templateKey = (progressionConfig ? progressionConfig.americana.template : 'americana_folk');

        if (templateKey === 'pop_standard') {
            const opts = ['pop_standard', 'emotional_ballad', 'royal_fanfare'];
            const idx = Math.floor((random ? random.next() : Math.random()) * opts.length);
            templateKey = opts[idx];
        } else if (templateKey === 'jazz_standard') {
            const opts = ['jazz_standard', 'neo_soul', 'gospel_turnaround'];
             const idx = Math.floor((random ? random.next() : Math.random()) * opts.length);
             templateKey = opts[idx];
        }

        // Task 1: Smart Template Resolution
        let templateObj = PROGRESSION_TEMPLATES[templateKey] || PROGRESSION_TEMPLATES.pop_standard;

        // Handle Legacy vs Smart Templates
        let variations = [];
        if (Array.isArray(templateObj)) {
            // Legacy format fallback
            variations = templateObj.map(seq => ({ seq, weight: 1.0 }));
        } else if (templateObj.variations) {
            // Smart format
            variations = templateObj.variations;
        } else {
             console.warn(`[MusicEngine] Invalid progression template for ${templateKey}.`);
             variations = PROGRESSION_TEMPLATES.pop_standard.variations;
        }

        // Filter and Weight Variations based on Context
        const intensity = (context.config && context.config.vectors) ? context.config.vectors.energy : 0.5;
        const currentSection = (context.structure && context.structure.length > 0) ? context.structure[0].type : 'VERSE';

        const validVariations = variations.filter(v => {
            if (!v.conditions) return true;
            if (v.conditions.intensityMin && intensity < v.conditions.intensityMin) return false;
            if (v.conditions.intensityMax && intensity > v.conditions.intensityMax) return false;
            if (v.conditions.section && !currentSection.includes(v.conditions.section)) return false;
            return true;
        });

        // Fallback if filtering removed everything
        const candidatePool = (validVariations.length > 0) ? validVariations : variations;

        // Weighted Selection
        let totalWeight = candidatePool.reduce((sum, v) => sum + (v.weight || 1.0), 0);
        let randomVal = (random ? random.next() : Math.random()) * totalWeight;
        let selectedVariation = candidatePool[0];

        for (const v of candidatePool) {
            if (randomVal < (v.weight || 1.0)) {
                selectedVariation = v;
                break;
            }
            randomVal -= (v.weight || 1.0);
        }

        // Extract the raw sequence (array of degrees)
        const template = (selectedVariation && selectedVariation.seq) ? selectedVariation.seq : (selectedVariation || [[0, 4, 5, 3]]);

        let flow = [];
        if (Array.isArray(template) && Array.isArray(template[0])) {
             // Pick one if it's a list of options
             flow = (random ? random.pick(template) : template[0]);
        } else if (Array.isArray(template)) {
             flow = template;
        } else {
             flow = [0, 4, 5, 3]; // Fallback
        }

        if (!Array.isArray(flow)) flow = [0, 4, 5, 3];

        const structure = context.structure || [{type: 'default', bars: 4}];
        const fullProgression = [];
        let currentBarOffset = 0;
        let currentBeatOffset = 0;

        const isFastChange = rString.includes('motorik') || rString.includes('baroque') || rString.includes('jazz');
        const isSlowChange = rString.includes('drone') || rString.includes('ambient') || rString.includes('post-rock');
        const isEpic = hString.includes('epic') || hString.includes('cinematic');
        const isFantasy = hString.includes('fantasy') || traits.name === 'Fantasy';
        const isRomantic = traits.name === 'Romantic' || traits.name === 'Impressionist';
        const isBaroque = traits.name === 'Baroque' || traits.name === 'Neo-Classical';
        const isMinimalist = traits.name === 'Minimalist';

        const allowBorrowed = !hString.includes('atonal') && !hString.includes('power');

        const isStrict = context.scaleType && profile.userScale !== 'Auto';
        // Defect Fix: Use profile vectors instead of legacy complexity
        const config = context.config || getSeededConfig('default', 12345, profile);

        const mutationProb = config.harmony.mutationChance;
        const borrowedProb = isStrict ? 0 : config.harmony.borrowedChance;

        structure.forEach((section, sectIdx) => {
            const sectionBars = (section.type === 'easter_egg') ? 4 : section.bars;

            // --- MICRO-FORM LOGIC (Round 4 - Phase 2) ---
            // Define an internal structure for the section (e.g. A-A-A-B).
            // A simple 4-bar unit usually has a turnaround at bar 4.
            // If section is 8 bars, bars 4 and 8 are turnarounds.
            // We'll mark these chords as 'isTurnaround'.

            const currentSectionKey = section.key || rootKey;
            const currentSectionScaleType = section.scale || scaleType;
            const currentScale = this.getScale(currentSectionKey, currentSectionScaleType);

            let flow = random ? random.pick(template) : template[Math.floor(Math.random() * template.length)];

            if (isBaroque && (section.type === 'BUILD' || section.type === 'VERSE' || section.type === 'GROOVE_A')) {
                flow = (progressionConfig ? progressionConfig.baroqueSequence : [0, 3, 6, 2, 5, 1, 4, 0]);
            }

            let flowIndex = 0;

            let ts = section.timeSig || context.timeSig || 4;
            if (Array.isArray(ts)) ts = ts[0];
            const sectionTimeSig = ts;

            for (let b = 0; b < sectionBars; b++) {
                let subBars = 1;
                if (isFastChange) subBars = 2;
                if (isSlowChange || isMinimalist) {
                    subBars = config.harmony.rhythm;
                    // Fix: Default to 4 if config failed
                    if (subBars < 1) subBars = 8;
                }

                // Fix: Only increment flowIndex based on subBars pacing
                if (isSlowChange || isMinimalist) {
                     // e.g. b=0 -> 0/8=0. b=7 -> 7/8=0. b=8 -> 1.
                     // Flow index should change every 'subBars' bars.
                     // Logic handled below in loop.
                }

                let subDivisions = 1;
                const hRhythm = traits.harmonicRhythm || (isFastChange ? 'fast' : 'moderate');

                switch (hRhythm) {
                    case 'hyper': subDivisions = 4; break;
                    case 'fast': subDivisions = 2; break;
                    case 'moderate': subDivisions = 1; break;
                    case 'slow_burn': subDivisions = 1; break;
                    case 'static': subDivisions = 1; break;
                    default: subDivisions = 1;
                }

                if (traits.harmonicRhythmMax) {
                    subDivisions = Math.min(subDivisions, traits.harmonicRhythmMax);
                }
                if (traits.name === 'Ethereal') subDivisions = 1;

                // Phrase Ending / Turnaround Logic
                const isPhraseEnd = (b + 1) % 4 === 0; // End of 4-bar block
                const isSectionEnd = (b === sectionBars - 1);

                // Increase harmonic rhythm at cadences
                if (isPhraseEnd && !isSlowChange && !isMinimalist && !isBaroque) {
                    subDivisions = Math.max(subDivisions, 2);
                }

                if (isSectionEnd && sectionBars >= 4) {
                    subDivisions = Math.max(subDivisions, 2);
                }

                if (section.type === 'BUILD' || section.type === 'TRANSITION' || section.type === 'RISE') {
                    subDivisions = Math.max(2, subDivisions * 2);
                }

                // Task 3: The "Pacing" Scalar (Duration Awareness)
                // Short Transition (<= 4 bars): Accelerate changes (Urgency)
                if (sectionBars <= 4) {
                    subDivisions = Math.max(subDivisions, 2); // Force 2 chords per bar minimum for short sections?
                    // Actually, if it's 4 bars, maybe normal speed is fine.
                    // If it's a 2-bar transition, we want movement.
                    if (sectionBars <= 2) subDivisions = Math.max(subDivisions, 2);
                }

                // Long Atmosphere (>= 16 bars): Decelerate changes (Space)
                // Override subBars to stretch progression
                // This logic is tricky inside the loop because 'flowIndex' logic below depends on 'subBars'.
                // We should adjust 'subBars' (how many bars per chord) instead of 'subDivisions' (how many chords per bar).

                if (sectionBars >= 16 && !isFastChange && !isBaroque) {
                    // Force Slow Change logic
                    subBars = Math.max(subBars, 4); // Hold each chord for 4 bars?
                    // This creates a very slow textural evolution.
                    // Only apply if it's not a 'driving' genre.
                    if (hRhythm !== 'hyper' && hRhythm !== 'fast') {
                         subDivisions = 1; // Ensure no rapid changes within bar
                    }
                }

                if (subDivisions > 4) subDivisions = 4;

                const duration = sectionTimeSig / subDivisions;
                const iterations = subDivisions;

                for (let s = 0; s < iterations; s++) {
                    let type = 'triad';

                    // Ensure flowIndex covers the full length of the progression flow
                    // If subBars = 1, we increment every bar.
                    // If subBars = 2, we increment every 2 bars (slow change).
                    if (!isSlowChange && !isMinimalist) {
                        flowIndex = (currentBarOffset + b + s); // Removed modulo here to allow linear progression through template
                    } else if (isSlowChange) {
                        // Increment only every 'subBars' bars
                        flowIndex = Math.floor((currentBarOffset + b) / subBars);
                    }

                    let degree = flow[flowIndex % flow.length];
                    let quality = 'major';
                    let rootPC = currentScale[degree % currentScale.length];

                    const r1 = random ? random.next() : Math.random();
                    const r2 = random ? random.next() : Math.random();

                    const mutationRoll = random ? random.next() : Math.random();
                    if (mutationRoll > (1.0 - mutationProb) && !isMinimalist && !isBaroque) {
                        const substitutes = FUNCTIONAL_SUBSTITUTIONS[degree];
                        if (substitutes && substitutes.length > 0) {
                            const subDegree = random ? random.pick(substitutes) : substitutes[Math.floor(Math.random() * substitutes.length)];

                            if (subDegree !== undefined) {
                                degree = subDegree;
                                rootPC = currentScale[degree % currentScale.length];

                                if (currentSectionScaleType === 'major') {
                                    if ([1, 2, 5].includes(degree)) quality = 'minor';
                                    else if (degree === 6) quality = 'diminished';
                                    else quality = 'major';
                                } else {
                                    if ([0, 3, 4].includes(degree)) quality = 'minor';
                                    else if (degree === 1) quality = 'diminished';
                                    else quality = 'major';
                                }
                            }
                        }
                    }

                    if (isRomantic && progressionConfig && degree === progressionConfig.romanticSubstitutions.degree && r1 > 0.6) {
                        rootPC = (currentScale[0] + 1) % 12;
                        quality = 'major';
                    }
                    else if (isEpic && (section.type === 'build' || section.type === 'climax') && r1 > 0.7) {
                        const shift = (random ? random.next() : Math.random()) > 0.5 ? 3 : -3;
                        rootPC = (rootPC + shift + 12) % 12;
                        quality = 'major';
                    }
                    else if (allowBorrowed && r2 > (1.0 - borrowedProb)) {
                        const isRomanticOrClassical = traits.name === 'Romantic' || traits.name.includes('Classical') || traits.name === 'Epic Cinematic';
                        const rangeMax = isRomanticOrClassical ? 6 : 4;
                        const borrowType = random ? random.range(0, rangeMax) : Math.floor(Math.random() * rangeMax);

                        if (currentSectionScaleType === 'major') {
                            if (borrowType === 0) {
                                rootPC = (currentScale[0] + 8) % 12;
                                quality = 'major';
                            } else if (borrowType === 1) {
                                rootPC = (currentScale[0] + 10) % 12;
                                quality = 'major';
                            } else if (borrowType === 2) {
                                rootPC = (currentScale[0] + 5) % 12;
                                quality = 'minor';
                            } else if (borrowType === 3) {
                                rootPC = (currentScale[0] + 1) % 12;
                                quality = 'major';
                            } else if (borrowType === 4 && isRomanticOrClassical) {
                                rootPC = (currentScale[0] + 2) % 12;
                                quality = 'major';
                                type = '7th';
                            } else if (borrowType === 5 && isRomanticOrClassical) {
                                rootPC = (currentScale[0] + 4) % 12;
                                quality = 'major';
                                type = '7th';
                            }
                        } else if (currentSectionScaleType === 'minor') {
                            if (borrowType === 0) {
                                rootPC = currentScale[0];
                                quality = 'major';
                            }
                            else if (borrowType === 1) {
                                rootPC = (currentScale[0] + 5) % 12;
                                quality = 'major';
                            }
                            else if (borrowType === 2 && isRomanticOrClassical) {
                                rootPC = (currentScale[0] + 2) % 12;
                                quality = 'diminished';
                            }
                            else {
                                rootPC = currentScale[0];
                                quality = 'major';
                            }
                        }
                    }
                    else {
                        if (currentSectionScaleType === 'major') {
                            if ([1, 2, 5].includes(degree)) quality = 'minor';
                            if (degree === 6) quality = 'diminished';
                        }
                        else if (currentSectionScaleType === 'minor' || currentSectionScaleType === 'harmonic_minor') {
                            if ([0, 3, 4].includes(degree)) quality = 'minor';
                            if ([2, 5, 6].includes(degree)) quality = 'major';
                            if (degree === 1) quality = 'diminished';
                        }
                    }

                    const isLastChord = (sectIdx === structure.length - 1) && (b === sectionBars - 1) && (s === iterations - 1);
                    if (isBaroque && currentSectionScaleType.includes('minor') && isLastChord) {
                        quality = 'major';
                        rootPC = currentScale[0];
                    }

                    const isDominant = (degree === 4 || degree === 10 || degree === 1);
                    const isTonic = (degree === 0);

                    if (chordTypePreference === 'extended') {
                         if (quality === 'major') {
                             if (isDominant) type = 'dom7';
                             else type = (random ? random.next() : Math.random()) > 0.5 ? 'maj7' : 'maj9';
                         }
                         if (quality === 'minor') type = (random ? random.next() : Math.random()) > 0.5 ? 'min7' : 'min9';

                         if (isTonic && (random ? random.next() : Math.random()) > 0.7) type = 'maj7';
                    } else if (chordTypePreference === 'power') {
                        type = 'power';
                        quality = 'power';
                    }

                    if (isFantasy && (quality === 'major' || quality === 'minor') && (random ? random.next() : Math.random()) > config.harmony.sus4Chance) {
                        type = 'sus4';
                    }

                    // Mark Turnarounds
                    // If this chord is in the last bar of a phrase/section, mark it.
                    // Note: 'b' is the bar index relative to section.
                    const isTurnaroundBar = isPhraseEnd || isSectionEnd;

                    fullProgression.push({
                        degree: degree,
                        scaleIndex: degree,
                        quality: quality,
                        type: type,
                        rootPitchClass: rootPC,
                        duration: duration,
                        absoluteBeat: currentBeatOffset + (b * sectionTimeSig) + (s * duration),
                        barIndex: currentBarOffset + b + (s / iterations),
                        section: section.type,
                        timeSig: sectionTimeSig,
                        key: currentSectionKey,
                        scale: currentSectionScaleType,
                        sectionProgress: (b + (s/iterations)) / sectionBars,
                        isTurnaround: isTurnaroundBar, // New Flag
                        isFill: isTurnaroundBar // Synonym for arrangement logic
                    });
                }
            }
            currentBeatOffset += (sectionBars * sectionTimeSig);
            currentBarOffset += sectionBars;
        });

        this.applyCadentialLogic(fullProgression, context);

        return fullProgression;
    }

    generateRhythmPatternEvents(role, patternArray, barStart, timeSig, strategy, pitch) {
        const numSteps = Math.ceil(timeSig * 4);
        const srcLen = patternArray.length;
        const events = [];

        for (let step = 0; step < numSteps; step++) {
            let srcIndex = step;

            if (strategy === 'loop') {
                srcIndex = step % srcLen;
            } else if (strategy === 'truncate') {
                if (step >= srcLen) continue;
                srcIndex = step;
            } else if (strategy === 'preserve_end') {
                if (srcLen > numSteps) {
                     const tailLen = 4;
                     const destBodyLen = numSteps - tailLen;

                     if (step >= destBodyLen) {
                         const offsetInTail = step - destBodyLen;
                         srcIndex = (srcLen - tailLen) + offsetInTail;
                     } else {
                         srcIndex = step;
                     }
                } else {
                     srcIndex = step % srcLen;
                }
            } else {
                srcIndex = step % srcLen;
            }

            if (srcIndex < 0) srcIndex = 0;
            if (srcIndex >= srcLen) srcIndex = srcIndex % srcLen;

            const hit = patternArray[srcIndex];
            if (hit) {
                events.push({
                    s: barStart + step * 0.25,
                    d: pitch,
                    len: 0.1,
                    vel: 0.9
                });
            }
        }
        return events;
    }

    applyRhythmPattern(role, patternArray, tracks, barStart, timeSig, strategy, pitch) {
        const events = this.generateRhythmPatternEvents(role, patternArray, barStart, timeSig, strategy, pitch);
        tracks[role].push(...events);
        // Important: this legacy method does NOT add to registry automatically because it lacks context.
        // The new flow avoids calling this directly or handles registry elsewhere.
    }

    applyVoicing(progression, traits, context, doublingStrategy = 'classic') {
        let vString = traits && traits.voicing ? traits.voicing.toLowerCase() : 'standard';

        let strategy = traits.voicingProfile || 'standard';

        if (!traits.voicingProfile) {
            if (vString.includes('cluster') || traits.name === 'Horror / Tension') strategy = 'cluster';
            else if (vString.includes('wide') || traits.name === 'Epic Cinematic') strategy = 'open';
            else if (vString.includes('close') || traits.name === 'Romantic') strategy = 'close';
            else if (traits.voicingStrategy) strategy = traits.voicingStrategy;
        }

        const isPower = strategy === 'power' || vString.includes('power');

        return progression.map(chord => {
            // Defect Fix: "The Sanitizer" - HarmonyManager's custom clusters were being overwritten by default voicing.
            // If the chord comes pre-loaded with special voicing (e.g. Horror Clusters), use it.
            if (chord.type === 'cluster' && chord.notes && chord.notes.length > 0) {
                // Determine register for the cluster
                // Horror wants Low and High.
                // HarmonyManager provides Pitch Classes (0-11) usually.
                // Let's assume we map them to a specific register here if they are < 12.
                let voicedNotes = [...chord.notes];
                if (voicedNotes[0] < 12) {
                    // Map to Low (C2) and High (C5)?
                    // Split cluster: half low, half high?
                    // Or just a low rumble + high shriek.
                    const low = voicedNotes.map(pc => pc + 36);
                    // Shift High notes by +1 semitone to create cross-register dissonance (Bitonality)
                    // This prevents octave doubling (0.0 score) and guarantees Minor 2nds/Major 7ths.
                    const high = voicedNotes.map(pc => pc + 84 + 1);
                    voicedNotes = [...low, ...high];
                }
                return { ...chord, notes: voicedNotes };
            }

            let formula = CHORD_FORMULAS[chord.type] || CHORD_FORMULAS.major;

            if (chord.type === 'sus4') formula = [0, 5, 7];

            if (isPower) formula = [0, 7];
            else if (strategy === 'quartal') formula = [0, 5, 10];
            else if (strategy === 'cluster') formula = [0, 1, 6]; // Horror Cluster (Root, b2, #4)
            else if (chord.type !== 'sus4') {
                if(chord.quality === 'minor' && chord.type === 'triad') formula = CHORD_FORMULAS.minor;
                if(chord.quality === 'major' && chord.type === 'triad') formula = CHORD_FORMULAS.major;
                if(chord.quality === 'diminished') formula = CHORD_FORMULAS.diminished;
            }

            let baseNotes = formula.map(interval => (chord.rootPitchClass + interval) % 12);
            let voicedNotes = baseNotes.map(pc => pc + 60);

            if (!isPower) {
                // Task 3: Harmonic Sprawl (Pass Freedom)
                const freedom = (context.config && context.config.vectors) ? context.config.vectors.harmonicComplexity : 0.5;
                voicedNotes = applyGenreVoicing(voicedNotes, strategy, freedom);
            }

            if (isPower) {
                voicedNotes = [baseNotes[0]+48, baseNotes[1]+48, baseNotes[0]+60];
            }

            // --- PHASE 5: Guitar Voicings ---
            // If voicing source implies guitar, apply specific spread
            if (traits && traits.name && (traits.name.includes('Folk') || traits.name.includes('Rock') || traits.name.includes('Western'))) {
                voicedNotes = applyGuitarVoicing(voicedNotes);
            }

            return { ...chord, notes: voicedNotes };
        });
    }

    generateAbstractTracks(voicedChords, traits, context, profile, allocationMap = {}, initialState = {}) {
        const tracks = {
            [ABSTRACT_ROLES.BASS]: [],
            [ABSTRACT_ROLES.HARMONY]: [], // Fallback
            [ABSTRACT_ROLES.HARMONY_PAD]: [],
            [ABSTRACT_ROLES.HARMONY_RHYTHMIC]: [],
            [ABSTRACT_ROLES.HARMONY_COLOR]: [],
            [ABSTRACT_ROLES.MELODY]: [],
            [ABSTRACT_ROLES.COUNTER_MELODY]: [],
            [ABSTRACT_ROLES.PERC_LOW]: [],
            [ABSTRACT_ROLES.PERC_MID]: [],
            [ABSTRACT_ROLES.PERC_HIGH]: []
        };

        // --- NEW: Global Voice Registry Initialization ---
        context.voiceRegistry = createRegistry();
        context.strategyConfig = { melody: MELODY_WEIGHTS, bass: BASS_WEIGHTS, bassConfig: BASS_CONFIG };

        // Task 1: Organic Lung (Stamina System)
        // If context.cache has no breath budgets, initialize them
        if (!context.cache.breathBudgets) {
            context.cache.breathBudgets = {};
        }

        // --- PRE-PROCESSING: Time-Slicing (Granularity Enforcer) ---
        // Ensure all chords are sliced into 1-bar segments to force per-bar evaluation
        // Constraint Fix: 1-Bar Resolution Limit.
        // We still slice to 1 bar to allow dynamic intensity updates,
        // BUT we inject 'phraseId' and 'segmentIndex' so RoleAllocator can stitch them back together for sustained roles.

        const expandedChords = [];
        voicedChords.forEach(chord => {
            const timeSig = chord.timeSig || 4;
            const duration = chord.duration || timeSig;
            // Tolerance for floating point
            const bars = Math.ceil((duration - 0.01) / timeSig);

            // Generate unique phrase ID for this multi-bar event
            // Use random or simple counter + timestamp
            // Fix: Use seeded random for determinism
            const rVal = (this.random ? this.random.next() : Math.random());
            const phraseId = `phrase_${Math.floor(rVal*100000)}`;

            if (bars <= 1) {
                // Even single bars get an ID, though segmentIndex is always 0
                expandedChords.push({ ...chord, phraseId, segmentIndex: 0, totalSegments: 1 });
            } else {
                for (let i = 0; i < bars; i++) {
                    const subChord = { ...chord };
                    subChord.barIndex = (chord.barIndex || 0) + i;
                    subChord.duration = timeSig; // Force 1 Bar

                    // Metadata for Continuity
                    subChord.phraseId = phraseId;
                    subChord.segmentIndex = i;
                    subChord.totalSegments = bars;
                    subChord.isContinuation = (i > 0);

                    if (subChord.absoluteBeat !== undefined) {
                        subChord.absoluteBeat = (chord.absoluteBeat || 0) + (i * timeSig);
                    }
                    // Update Tension for this specific slice
                    if (context.getTension) {
                        subChord.tension = context.getTension(subChord.barIndex);
                    }
                    expandedChords.push(subChord);
                }
            }
        });

        const getBreathBudget = (role) => {
            if (!context.cache.breathBudgets[role]) {
                // Initialize new budget based on Instrument Trait
                let lungCapacity = 'average'; // Default
                const allocation = allocationMap[role];
                if (allocation && allocation.length > 0 && allocation[0].instrument) {
                    lungCapacity = allocation[0].instrument.lung_capacity || 'average';
                }

                let base = 8; // 2 bars (average)
                if (lungCapacity === 'infinite') base = Infinity;
                else if (lungCapacity === 'athletic') base = 16; // 4 bars
                else if (lungCapacity === 'average') base = 8;   // 2 bars
                else if (lungCapacity === 'shallow') base = 4;   // 1 bar

                // Variance +/- 20% (only if finite)
                let budget = base;
                if (base !== Infinity) {
                    const variance = base * 0.2;
                    budget = base + ((this.random.next() * 2 - 1) * variance);
                }
                context.cache.breathBudgets[role] = budget;
            }
            return context.cache.breathBudgets[role];
        };

        const updateBreathBudget = (role, cost, tension) => {
            if (tension > 0.8) return; // Infinite lung in high tension
            let budget = getBreathBudget(role);
            if (budget === Infinity) return;
            budget -= cost;
            context.cache.breathBudgets[role] = budget;
        };

        const checkStamina = (role, tension) => {
            if (tension > 0.8) return true; // Always allowed in climax
            const budget = getBreathBudget(role);
            if (budget === Infinity) return true;

            if (budget <= 0) {
                // Exhausted. Force Recharge (Rest).
                return false;
            }
            return true;
        };

        // Helper: Polyrhythmic Lung (Staggered Breathing) - Legacy function kept for structural breaths?
        // Replacing with Stamina System usage in adaptEvents or filtering.

        const rString = traits ? traits.rhythm.toLowerCase() : 'basic';
        let grooveKey = 'standard_4_4';
        if (rString.includes('motorik')) grooveKey = 'motorik';
        if (rString.includes('waltz')) grooveKey = 'waltz';
        if (rString.includes('swing')) grooveKey = 'swing_jazz';

        const timeSig = context.timeSig || 4;

        let patternKey = traits.rhythmPattern || 'basic_rock';
        if (!traits.rhythmPattern) {
            if (rString.includes('four')) patternKey = 'four_on_floor';
            if (rString.includes('swing')) patternKey = 'swing_jazz';
            if (rString.includes('metal')) patternKey = 'metal_blast';
        }

        let harmPatternKey = 'sustained';
        if (rString.includes('rock')) harmPatternKey = 'chug_8ths';

        const PATTERN_MAP = {
            'alberti': 'alberti',
            'nocturne': 'nocturne'
        };
        if (PATTERN_MAP[traits.voicingStrategy]) {
            harmPatternKey = PATTERN_MAP[traits.voicingStrategy];
        }

        if (traits.name === 'Romantic' && !harmPatternKey) harmPatternKey = 'nocturne';
        if (timeSig === 3) harmPatternKey = 'waltz_dotted';

        let activePattern = MOTIF_PATTERNS.default;

        const sectionMotifMap = {};
        const availablePatterns = Object.values(MOTIF_PATTERNS);

        const pickPatternForSection = (sectionType) => {
             const type = sectionType.toUpperCase();
             let options = [MOTIF_PATTERNS.default];

             // DATA-DRIVEN: Check for explicit motifOptions in traits
             if (traits.motifOptions && Array.isArray(traits.motifOptions)) {
                 const mapped = traits.motifOptions.map(k => MOTIF_PATTERNS[k]).filter(Boolean);
                 if (mapped.length > 0) options = mapped;
             }
             // LEGACY FALLBACKS (To be deprecated via GenreLibrary updates)
             else if (traits.name && traits.name.includes('Fanfare')) {
                 options = [MOTIF_PATTERNS.fanfare, MOTIF_PATTERNS.triplets, MOTIF_PATTERNS.heroic_leap];
             }
             else if (traits.name && traits.name.includes('Action')) {
                 options = [MOTIF_PATTERNS.staccato_run, MOTIF_PATTERNS.riff_short, MOTIF_PATTERNS.syncopated, MOTIF_PATTERNS.motor_16ths];
             }
             else if (traits.name && (traits.name.includes('Romantic') || traits.name.includes('Lyrical') || traits.name.includes('Cinematic'))) {
                 options = [MOTIF_PATTERNS.lyrical_long, MOTIF_PATTERNS.slow, MOTIF_PATTERNS.heroic_leap, MOTIF_PATTERNS.waltz_feel];
             }
             else if (traits.name && traits.name.includes('Rock')) {
                 options = [MOTIF_PATTERNS.riff_short, MOTIF_PATTERNS.chug_8ths, MOTIF_PATTERNS.syncopated];
             }
             else {
                 options = [MOTIF_PATTERNS.default, MOTIF_PATTERNS.syncopated, MOTIF_PATTERNS.fragmented];
             }

             if (type.includes('CHORUS') || type.includes('CLIMAX')) {
                 options = options.filter(p => p.length > 3);
                 if(options.length === 0) options = [MOTIF_PATTERNS.syncopated];
             } else if (type.includes('VERSE') || type.includes('INTRO')) {
                 options = options.filter(p => p.length <= 4);
                 if(options.length === 0) options = [MOTIF_PATTERNS.default];
             }

             return options[Math.floor((this.random ? this.random.next() : Math.random()) * options.length)];
        };

        if (context.structure) {
            context.structure.forEach(s => {
                if (!sectionMotifMap[s.type]) {
                    sectionMotifMap[s.type] = pickPatternForSection(s.type);
                }
            });
        }

        const globalPattern = pickPatternForSection('VERSE');

        // Task 3: Voice Leading State
        let prevHarmonyPitch = initialState.lastHarmonyNote || 60;

        // Defect Fix: Breathing Enforcer (Active Voice Check)
        const activeRoleCount = Object.keys(allocationMap).filter(r => allocationMap[r] && allocationMap[r].length > 0).length;
        const isCrowded = activeRoleCount > 3;

        expandedChords.forEach((chord, chordIndex) => {
            const barIndex = (chord.barIndex !== undefined ? chord.barIndex : chordIndex);

            let accumulatedBeats = 0;
            let barsCounted = 0;
            let found = false;
            let thisBarTimeSig = timeSig;

            for(let s of context.structure) {
                const sBars = s.bars || 4;
                const sTs = s.timeSig || timeSig;

                if (barIndex < barsCounted + sBars) {
                    const barsIntoSection = barIndex - barsCounted;
                    accumulatedBeats += barsIntoSection * sTs;
                    thisBarTimeSig = sTs;
                    found = true;
                    break;
                }

                accumulatedBeats += sBars * sTs;
                barsCounted += sBars;
            }

            if (!found) {
                accumulatedBeats = barIndex * timeSig;
            }

            let barStart = accumulatedBeats;
            const effectiveTimeSig = thisBarTimeSig;

            // =================================================================
            // REFACTORED GENERATION ORDER: Rhythm -> Harmony -> Melody
            // =================================================================

            // -----------------------------------------------------------------
            // STEP 1: DRUMS & PERCUSSION
            // -----------------------------------------------------------------
            // Get Pattern Set early for Kick sync
            const secType = chord.section || 'VERSE';
            let currentPatternKey = patternKey;
            if (context.config && context.config.arrangement && context.config.arrangement[secType]) {
                currentPatternKey = context.config.arrangement[secType].drums || patternKey;
            }
            let patternSet = null;
            if (!getDrumStrategy(currentPatternKey)) {
                patternSet = RHYTHM_PATTERNS[currentPatternKey];
                // Resolve pattern from object (if new structure)
                if (patternSet && !Array.isArray(patternSet.kick) && patternSet.kick === undefined) {
                     // Not a direct pattern object?
                     // Check if it's the new style with 'pattern' or 'kick' properties.
                     // The new style has `kick: [], snare: []`.
                     // My refactor kept `kick: [...]`.
                     // BUT I might have wrapped them?
                     // In `theoryConstants.js`, `RHYTHM_PATTERNS.basic_rock` is `{ kick: [...], snare: [...] }`.
                     // This matches the OLD structure effectively, just with tags added.
                     // So usage `patternSet.kick` is valid.
                }

                if (!patternSet) patternSet = RHYTHM_PATTERNS[patternKey] || RHYTHM_PATTERNS.basic_rock;
            }

            const proceduralDrumGen = getDrumStrategy(currentPatternKey);
            const nextChord = expandedChords[chordIndex + 1];
            const isLastBarOfSection = nextChord && nextChord.section !== chord.section;
            const bassRhythmContext = []; // Stores relative times for synchronization

            // Fix: "Mainline" - Pass Narrative Tension (Defined in outer scope for all steps)
            const narrativeTension = context.getTension ? context.getTension(barIndex) : 0.5;

            if (proceduralDrumGen && !isLastBarOfSection) {
                const drumEvents = proceduralDrumGen(chord, barStart, {
                    random: this.random,
                    config: context.config,
                    bassRhythm: [], // Bass not yet generated
                    isLastBar: isLastBarOfSection,
                    narrativeTension: narrativeTension // Inject into context
                });

                drumEvents.forEach(ev => {
                    ev.barIndex = barIndex; // Inject Bar Index for RoleAllocator
                    if (tracks[ev.role]) tracks[ev.role].push(ev);
                });
                addToRegistry(context.voiceRegistry, drumEvents);
            }
            else {
                let generatedPercEvents = [];
                if (isLastBarOfSection && context.ensembleKey !== 'string_quartet') {
                    const fillEvents = generateDrumFill(barStart, chord.section === 'BUILD' ? 'high' : 'medium', context);
                    fillEvents.forEach(ev => {
                        ev.barIndex = barIndex;
                        if (tracks[ev.role]) tracks[ev.role].push(ev);
                        generatedPercEvents.push(ev);
                    });
                    const crash = {s: barStart, d:36, len:0.1, vel:0.9, role: ABSTRACT_ROLES.PERC_LOW, barIndex: barIndex};
                    tracks[ABSTRACT_ROLES.PERC_LOW].push(crash);
                    generatedPercEvents.push(crash);
                }
                else if (context.ensembleKey === 'orchestra' && (traits.name.includes('Action') || traits.name.includes('Epic'))) {
                    const intensity = (chord.section === 'CLIMAX' || chord.section === 'DROP') ? 'high' : 'med';
                    const battContext = {
                        profile: traits.percussionProfile,
                        timeSignature: [context.timeSig || 4, 4],
                        bassRhythm: []
                    };
                    if (context.structure[0].timeSignature) {
                        battContext.timeSignature = [context.structure[0].timeSignature.numerator, context.structure[0].timeSignature.denominator];
                    }
                    const battery = generateActionBattery(barStart, intensity, this.random, battContext);
                    battery.forEach(ev => {
                        ev.barIndex = barIndex;
                        if (tracks[ev.role]) tracks[ev.role].push(ev);
                        generatedPercEvents.push(ev);
                    });
                }
                else if (patternSet) {
                    let strategy = 'loop';
                    if (timeSig < 4) {
                        const isMath = traits.name === 'Math Rock' || traits.name === 'Progressive';
                        strategy = isMath ? 'truncate' : 'preserve_end';
                    }
                    if (patternSet.kick) {
                        const kicks = this.generateRhythmPatternEvents(ABSTRACT_ROLES.PERC_LOW, patternSet.kick, barStart, effectiveTimeSig, strategy, 36);
                        kicks.forEach(e => e.barIndex = barIndex);
                        tracks[ABSTRACT_ROLES.PERC_LOW].push(...kicks);
                        generatedPercEvents.push(...kicks);
                    }
                    if (context.ensembleKey === 'orchestra') {
                        const isClimax = chord.section === 'CLIMAX' || chord.section === 'DROP';
                        if (isClimax) {
                            if (barIndex % 2 === 0) {
                                const hit = {s: barStart, d:49, len:1.0, vel:1.0, role: ABSTRACT_ROLES.PERC_HIGH, barIndex: barIndex};
                                tracks[ABSTRACT_ROLES.PERC_HIGH].push(hit);
                                generatedPercEvents.push(hit);
                            }
                        }
                    } else {
                        if (patternSet.snare) {
                             const snares = this.generateRhythmPatternEvents(ABSTRACT_ROLES.PERC_MID, patternSet.snare, barStart, effectiveTimeSig, strategy, 38);
                             snares.forEach(e => e.barIndex = barIndex);
                             tracks[ABSTRACT_ROLES.PERC_MID].push(...snares);
                             generatedPercEvents.push(...snares);
                        }
                    }
                }
                addToRegistry(context.voiceRegistry, generatedPercEvents);
            }

            // Defect Fix: Extract Kick Pattern for Bass Sync
            // Filter current bar kicks from global track
            const currentBarKicks = tracks[ABSTRACT_ROLES.PERC_LOW].filter(e => e.s >= barStart && e.s < barStart + (chord.duration || 4));

            // -----------------------------------------------------------------
            // STEP 2: BASS (Foundation)
            // -----------------------------------------------------------------
            let bassStrategyName = traits ? traits.bassStrategy : null;

            if (!bassStrategyName && !traits.name.includes('Baroque') && !traits.name.includes('Minimalist')) {
                if (context.config && context.config.arrangement && context.config.arrangement[secType]) {
                    bassStrategyName = context.config.arrangement[secType].bass;
                }
            }

            const bassGenerator = bassStrategyName ? getBassStrategy(bassStrategyName) : null;
            const isPolyrhythmic = traits.name === 'Action/Chase' || traits.name === 'Math Rock';
            const isBaroque = traits.name === 'Baroque' || traits.name === 'Neo-Classical';

            let currentBassEvents = [];

            if (bassGenerator) {
                // Determine Bass Instrument for context injection (Envelope Awareness)
                const bassInst = allocationMap[ABSTRACT_ROLES.BASS] && allocationMap[ABSTRACT_ROLES.BASS].length > 0
                                 ? allocationMap[ABSTRACT_ROLES.BASS][0].instrument
                                 : null;

                // Fix: "Mainline" - Pass narrative tension to Bass Strategy
                const narrativeTension = context.getTension ? context.getTension(barIndex) : 0.5;

                currentBassEvents = bassGenerator(chord, barStart, {
                    syncopationLevel: traits.syncopationLevel,
                    kickPattern: patternSet ? patternSet.kick : null,
                    kickPatternEvents: currentBarKicks, // Inject absolute kicks
                    nextChord: expandedChords[chordIndex + 1],
                    random: this.random,
                    traits: traits,
                    cache: context.cache,
                    analysis: null, // Removed dependency on Melody Analysis for now
                    instrument: bassInst,
                    voiceRegistry: context.voiceRegistry, // Inject Registry
                    config: context.strategyConfig, // Inject Config
                    tension: narrativeTension // Inject Narrative Tension
                });
            } else {
                 const stepwiseGen = getBassStrategy('stepwise');
                 const useLineCliche = (traits.name && traits.name.includes('Fantasy')) && chord.quality === 'minor' && Math.random() > 0.7;

                 if (isBaroque) {
                     const baroqueGen = getBassStrategy('baroque_walking');
                     if (baroqueGen) currentBassEvents = baroqueGen(chord, barStart, { nextChord });
                 }
                 else if (useLineCliche) {
                     const clicheGen = getBassStrategy('line_cliche');
                     if(clicheGen) currentBassEvents = clicheGen(chord, barStart, {});
                 }
                 else if (!isPolyrhythmic && !bassStrategyName && stepwiseGen) {
                     currentBassEvents = stepwiseGen(chord, barStart, { nextChord });
                 }
                 else if (isPolyrhythmic) {
                    currentBassEvents = generateOstinato(chord, barStart, 'driving', this.random, {
                        rate: '8n', barStart, rootKey: context.rootKey, range: {min: 36, max: 48},
                        voiceRegistry: context.voiceRegistry
                    });
                 } else {
                    currentBassEvents = [{
                        s: barStart,
                        d: chord.rootPitchClass + 36,
                        len: chord.duration,
                        vel: 0.9,
                        notes: [chord.rootPitchClass + 36]
                    }];
                 }
            }

            // --- Defect Fix: Universal Kick Lock (Rhythmic Cohesion) ---
            // Ensure Bass locks to Kick for high-energy genres, regardless of strategy used.
            const isEpic = traits.name && (traits.name.includes('Epic') || traits.name.includes('Cinematic'));
            const isDriving = (traits.rhythm && (traits.rhythm.includes('driving') || traits.rhythm.includes('action'))) || narrativeTension > 0.6 || isEpic;

            // Filter out ghost notes (low velocity) to avoid locking to rolls
            const strongKicks = currentBarKicks.filter(k => (k.vel || 0.7) > 0.4);

            if (isDriving && strongKicks.length > 0) {
                 const ATTRACTION_WINDOW = 0.1; // Matched to ScoringEngine
                 const rootPitch = chord.rootPitchClass + (BASS_CONFIG.octaves ? BASS_CONFIG.octaves.default : 36);

                 // 1. Snap existing notes
                 currentBassEvents = currentBassEvents.map(ev => {
                     const closeKick = strongKicks.find(k => Math.abs(k.s - ev.s) < ATTRACTION_WINDOW);
                     if (closeKick) return { ...ev, s: closeKick.s };
                     return ev;
                 });

                 // 2. Inject missing notes (Strict Lock)
                 strongKicks.forEach(k => {
                     const hasBass = currentBassEvents.some(b => Math.abs(b.s - k.s) < 0.05); // Strict check
                     if (!hasBass) {
                         currentBassEvents.push({
                             s: k.s,
                             d: rootPitch,
                             len: 0.25,
                             vel: 0.9,
                             notes: [rootPitch],
                             articulation: 'short',
                             role: ABSTRACT_ROLES.BASS
                         });
                     }
                 });
                 currentBassEvents.sort((a,b) => a.s - b.s);
            }

            currentBassEvents.forEach(ev => ev.barIndex = barIndex);
            tracks[ABSTRACT_ROLES.BASS].push(...currentBassEvents);
            addToRegistry(context.voiceRegistry, currentBassEvents);
            currentBassEvents.forEach(e => bassRhythmContext.push(e.s - barStart));

            // -----------------------------------------------------------------
            // STEP 3: HARMONY (SPLIT VOICES)
            // -----------------------------------------------------------------
            // Refactor: Harmony Split into 3 Distinct Voices (Pad, Rhythmic, Color)

            // --- VOICE A: PAD / SUSTAIN (Bed) ---
            const padEvents = [];
            // Default Pad Behavior: Sustained chords
            // Special handling for Sci-Fi/Horror where "Pad" might be Aleatoric
            if (traits.name === 'Horror / Tension' || traits.name === 'Avant-Garde') {
                // Defect Fix: "Metric Blindness" - Aleatoric Clouds were too sparse for ScoringEngine (monophonic).
                // If tension is high, use a Cluster Block (Polyphonic) to trigger Dissonance Metric.
                if (chord.tension > 0.7 || chord.section === 'CLIMAX') {
                     const clusterEvents = generateCluster(chord, barStart, chord.duration || 4.0);
                     padEvents.push(...clusterEvents);
                } else {
                    // Even low tension Horror needs polyphony for the dissonance metric.
                    // Use a Quiet Cluster (Drone) instead of sparse clouds.
                    const clusterEvents = generateCluster(chord, barStart, chord.duration || 4.0);
                    clusterEvents.forEach(e => {
                        e.vel = 0.3; // Quiet
                        e.articulation = 'long';
                        e.role = ABSTRACT_ROLES.HARMONY_PAD;
                    });
                    padEvents.push(...clusterEvents);
                }
            } else {
                // Standard Pad
                // Fix: Shift down for Cinematic to boost Low Voice Distribution
                const isCinematic = traits.name && (traits.name.includes('Epic') || traits.name.includes('Cinematic'));
                const isImpressionist = traits.name && traits.name === 'Impressionist';
                const padOctave = (isCinematic || isImpressionist) ? 36 : 48; // C2 vs C3

                padEvents.push({
                    s: barStart,
                    d: chord.rootPitchClass + padOctave,
                    notes: chord.notes,
                    len: (chord.duration || 4.0) - 0.1, // Ensure breathing room
                    vel: 0.6, // Soft bed
                    satb: chord.satb,
                    role: ABSTRACT_ROLES.HARMONY_PAD,
                    articulation: 'legato' // Force legato
                });
            }
            padEvents.forEach(ev => ev.barIndex = barIndex);
            tracks[ABSTRACT_ROLES.HARMONY_PAD].push(...padEvents);
            addToRegistry(context.voiceRegistry, padEvents);

            // --- VOICE B: RHYTHMIC / COMPING (Pulse) ---
            const rhythmEvents = [];

            // Defect Fix: Breathing Enforcer (Crowd Control)
            // Legacy Logic Removed: Moved to GenerationPipeline.stepBreathingEnforcer for smart rotation.
            // We set skipRhythm to false here to allow GenerationPipeline to handle muting.
            const skipRhythm = false;

            // Phase 1 Audit: Cadence Awareness
            // If this chord marks a Cadential Resolution (e.g. Perfect V->I),
            // Override texture to 'swell' or 'resolution' to support the landing.
            const isResolution = chord.isResolution;
            const cadenceType = chord.cadenceType; // Note: cadenceType is on PREP chord, isResolution on TARGET

            if (isResolution && !isPolyrhythmic) {
                 // Force a Swell/Resolution texture
                 rhythmEvents.push({
                     s: barStart,
                     d: chord.rootPitchClass + 60,
                     notes: chord.notes,
                     rootPitchClass: chord.rootPitchClass,
                     len: chord.duration || 4.0,
                     role: ABSTRACT_ROLES.HARMONY_RHYTHMIC,
                     type: 'INSTRUCTION',
                     texture: 'swell', // Orchestral Swell or Fade-in
                     section: chord.section,
                     timeSig: context.timeSig,
                     tension: narrativeTension
                 });
            }
            else if (!skipRhythm && isPolyrhythmic) {
                 let ratio = '3:2';
                 if (effectiveTimeSig >= 3 && effectiveTimeSig % 1 !== 0) { } // Keep simple
                 else {
                     const num = context.structure[0].timeSignature ? context.structure[0].timeSignature.numerator : 4;
                     if (num === 5) ratio = '5:4';
                 }
                 // Defer to RoleAllocator for unique seeding
                 rhythmEvents.push({
                     s: barStart,
                     d: chord.rootPitchClass + 60, // approximate center
                     notes: chord.notes,
                     rootPitchClass: chord.rootPitchClass,
                     len: chord.duration || 4.0,
                     role: ABSTRACT_ROLES.HARMONY_RHYTHMIC,
                     type: 'INSTRUCTION',
                     texture: 'polyrhythm',
                     pattern: ratio,
                     section: chord.section,
                     timeSig: context.timeSig,
                     tension: narrativeTension
                 });
            }
            else if (context.ensembleKey === 'orchestra') {
                // Orchestral Ostinatos live here
                const progress = chord.sectionProgress || 0;
                let ostPattern = 'driving';
                if (progress < 0.3) ostPattern = 'pulsing';
                else if (progress < 0.6) ostPattern = 'action';

                // NEW: Use RhythmLibrary for variety
                if (traits.name.includes('Action') || traits.name.includes('Epic')) {
                    // Try to pick a random pattern 50% of the time, or stick to defaults
                    if (this.random.bool(0.5)) {
                        const libPattern = getRandomPattern(context.timeSig || 4, 'action', this.random ? { next: () => this.random.next() } : null);
                        if (libPattern) ostPattern = libPattern;
                    }
                }

                // Fix: Shift Ostinato Register for Cinematic
                const isCinematic = traits.name && (traits.name.includes('Epic') || traits.name.includes('Cinematic') || traits.name.includes('Action'));
                // C4 (60) is Mid.
                // C3 (48) is Low-Mid (Cellos/Violas).
                // C2 (36) is Low (Basses/Cellos).

                // Adjustment: Use C5 (72) for Epic to ensure High Energy (Strings Ostinato)
                // Bass covers Low (36-48). Pads cover Mid (48-60).
                const ostinatoPitch = isCinematic ? chord.rootPitchClass + 72 : chord.rootPitchClass + 60;

                // Defer to RoleAllocator (The "Clone Wars" Fix)
                rhythmEvents.push({
                     s: barStart,
                     d: ostinatoPitch,
                     notes: chord.notes,
                     rootPitchClass: chord.rootPitchClass,
                     len: chord.duration || 4.0,
                     role: ABSTRACT_ROLES.HARMONY_RHYTHMIC,
                     type: 'INSTRUCTION',
                     texture: 'ostinato',
                     pattern: ostPattern,
                     section: chord.section,
                     timeSig: context.timeSig,
                     tension: narrativeTension
                });

                if (chord.section === 'CLIMAX') {
                    rhythmEvents.push({
                         s: barStart,
                         d: chord.rootPitchClass + 48,
                         notes: chord.notes,
                         rootPitchClass: chord.rootPitchClass,
                         len: chord.duration || 4.0,
                         role: ABSTRACT_ROLES.HARMONY_RHYTHMIC,
                         type: 'INSTRUCTION',
                         texture: 'stabs',
                         section: chord.section,
                         timeSig: context.timeSig,
                         tension: narrativeTension
                    });
                }
            }
            else if (traits.rhythm.includes('rock') || traits.rhythm.includes('funk') || traits.rhythm.includes('pop')) {
                // Locked Groove
                const kickHits = patternSet && patternSet.kick ? patternSet.kick.reduce((acc, val, i) => {
                    if (val) acc.push(i * 0.25);
                    return acc;
                }, []) : [];
                const lockedEvents = generateLockedGroove(chord, kickHits, 'lock', { barStart, traits: traits });
                lockedEvents.forEach(e => { e.role = ABSTRACT_ROLES.HARMONY_RHYTHMIC; });
                rhythmEvents.push(...lockedEvents);
            }
            else if (!skipRhythm) {
                // General Rhythmic Comping fallback
                const baseDensity = (context.strategyConfig && context.strategyConfig.melody && context.strategyConfig.melody.harmony)
                                    ? context.strategyConfig.melody.harmony.baseDensity : 0.3;

                const densityVector = (context.config && context.config.vectors) ? context.config.vectors.melodicDensity : 0.5;
                // Defect Fix: Use RhythmicDensity for density checks
                const rDensity = (context.config && context.config.vectors) ? context.config.vectors.rhythmicDensity : 0.5;
                const effectiveDensity = baseDensity * (densityVector * 2.0);

                // Task 3: Duration Bias (Legato Glue) & Defect Fix: Hard Density Cap
                // If RhythmicComplexity < 0.7, force 1n/2n. Ban 8th notes unless Tension is maxed.
                const highTension = (chord.tension || 0) > 0.8;

                // Logic Trap Fix: Genre-Aware Legato
                // Only force legato (sustain) if the genre explicitly demands it (Ambient, Ethereal)
                // OR if the density is dangerously low (< 0.2) where rhythmic activity implies silence.
                const isSustainGenre = traits.name.includes('Ambient') || traits.name.includes('Ethereal') || traits.name.includes('Atmosphere') || traits.rhythm.includes('fluid');
                const forceLegato = (isSustainGenre && densityVector < 0.6) || (densityVector < 0.2 && !highTension);

                // Defect Fix: Double-Pad Issue.
                // If density is very low (< 0.3), Voice B (Rhythmic) should be tacit, relying on Voice A (Pad).
                if (effectiveDensity < TACIT_DENSITY_THRESHOLD && !highTension && !isHighIntensity) {
                    // Tacit (Silence)
                    // Do nothing -> Empty rhythmEvents
                }
                else if (effectiveDensity < LEGATO_DENSITY_THRESHOLD || forceLegato) {
                     // Fix: Harmony Flatline - Use "Gentle Pulse" Instruction for Low Density
                     // Unless explicitly a Sustain Genre (Ambient, Ethereal)
                     if (!isSustainGenre && !forceLegato) {
                         // Use Texture Engine to unpack into gentle pulses
                         rhythmEvents.push({
                             s: barStart,
                             d: chord.rootPitchClass + 60,
                             notes: chord.notes,
                             rootPitchClass: chord.rootPitchClass,
                             len: chord.duration || 4.0,
                             role: ABSTRACT_ROLES.HARMONY_RHYTHMIC,
                             type: 'INSTRUCTION',
                             texture: 'ostinato',
                             pattern: 'pulsing', // Gentle quarter/eighth pulse
                             section: chord.section,
                             timeSig: context.timeSig,
                             tension: narrativeTension
                         });
                     } else {
                         // Legato Pad / Whole Notes (Fallback for Ambient or forced Legato)
                         // Force 1n or 2n
                         const noteLen = (rDensity < 0.4) ? (chord.duration || 4.0) : 2.0;
                         const count = Math.floor((chord.duration || 4.0) / noteLen);

                         for(let i=0; i<count; i++) {
                             // Task 3: Voice Leading
                             let targetPitch = this.getNearestChordTone(chord, prevHarmonyPitch);

                             // Task 1: Voice Leading Gate
                             const vlContext = {
                                 tension: chord.tension || 0.5,
                                 role: 'harmony',
                                 key: context.rootKey,
                                 scaleType: context.scaleType,
                                 // melodyDirection? Need state tracking. Ignoring for now or pass neutral.
                             };

                             if (!validateVoiceLeading(prevHarmonyPitch, targetPitch, vlContext)) {
                                 // Vetoed! Try to find a valid alternate.
                                 // Get all chord tones in valid range
                                 const candidates = [];
                                 const chordNotes = chord.notes || [chord.rootPitchClass];
                                 // Expand to range centered on prevHarmonyPitch
                                 const rangeCenter = prevHarmonyPitch;
                                 [-1, 0, 1].forEach(oct => {
                                     chordNotes.forEach(n => {
                                         candidates.push((n % 12) + (Math.floor(rangeCenter/12) + oct) * 12);
                                     });
                                 });

                                 // Sort by distance
                                 candidates.sort((a,b) => Math.abs(a - rangeCenter) - Math.abs(b - rangeCenter));

                                 // Find first valid candidate
                                 const validAlt = candidates.find(c => validateVoiceLeading(prevHarmonyPitch, c, vlContext));
                                 if (validAlt) targetPitch = validAlt;
                             }

                             prevHarmonyPitch = targetPitch;

                             rhythmEvents.push({
                                s: barStart + (i * noteLen),
                                d: targetPitch,
                                notes: chord.notes,
                                len: noteLen,
                                vel: 0.6,
                                role: ABSTRACT_ROLES.HARMONY_RHYTHMIC,
                                articulation: 'legato',
                                tension: narrativeTension // Inject Narrative Tension for Smart Thinning
                             });
                         }
                     }
                }
                else {
                    // Fix: Centralized Rhythm Generation
                    // Stop generating pre-baked patterns here (which forces unison).
                    // Instead, output a single long note (the canvas) and let RoleAllocator
                    // carve it up using unique seeds/hashes.
                    // REFACTOR: Removed 'INSTRUCTION' type to allow RoleAllocator to use its native
                    // Euclidean 'rhythmic_comp' strategy, which handles sparse density correctly.
                    rhythmEvents.push({
                        s: barStart,
                        d: chord.rootPitchClass + 48,
                        notes: chord.notes,
                        len: chord.duration || 4.0, // Full bar duration
                        vel: 0.7,
                        satb: chord.satb,
                        role: ABSTRACT_ROLES.HARMONY_RHYTHMIC,
                        // Removed type: 'INSTRUCTION' and texture: 'generic_comp'
                        tension: narrativeTension
                    });
                }
            }
            rhythmEvents.forEach(ev => ev.barIndex = barIndex);
            tracks[ABSTRACT_ROLES.HARMONY_RHYTHMIC].push(...rhythmEvents);
            addToRegistry(context.voiceRegistry, rhythmEvents);

            // --- VOICE C: COLOR / TEXTURE (Sparkle) ---
            const colorEvents = [];
            const sciFiConfig = context.config ? context.config.sciFi : null;

            if (traits.name === 'Sci-Fi' && barIndex >= (context.structure[0].bars || 8) / 2 && sciFiConfig) {
                 const arpEvents = generateArpeggio(chord, sciFiConfig.highArp.pattern, null, {
                     barStart,
                     barIndex,
                     scaleType: context.scaleType,
                     rootKey: context.rootKey,
                     range: { min: sciFiConfig.highArp.rangeMin, max: sciFiConfig.highArp.rangeMax }
                 });
                 arpEvents.forEach(e => { e.role = ABSTRACT_ROLES.HARMONY_COLOR; e.vel = 0.5; });
                 colorEvents.push(...arpEvents);
            }
            else if (harmPatternKey === 'alberti' || harmPatternKey === 'arp_up') {
                 const arpEvents = generateArpeggio(chord, [{s:0,l:0.5},{s:0.5,l:0.5},{s:1,l:0.5},{s:1.5,l:0.5},{s:2,l:0.5},{s:2.5,l:0.5},{s:3,l:0.5},{s:3.5,l:0.5}], null, {
                     barStart, barIndex, scaleType: context.scaleType, rootKey: context.rootKey,
                     random: this.random
                 });
                 arpEvents.forEach(e => { e.role = ABSTRACT_ROLES.HARMONY_COLOR; });
                 colorEvents.push(...arpEvents);
            }
            else {
                 // Sparse decorative arps for Color
                 // Fix: Boost Highs for Cinematic to improve Voice Distribution
                 const isCinematic = traits.name && (traits.name.includes('Epic') || traits.name.includes('Cinematic'));
                 const colorChance = isCinematic ? 1.0 : 0.3; // Force color for Cinematic to ensure Highs

                 if (this.random.bool(colorChance)) {
                      const arpEvents = generateArpeggio(chord, [{s:0,l:0.25},{s:0.25,l:0.25},{s:0.5,l:0.25},{s:0.75,l:0.25}], null, {
                          barStart, barIndex, scaleType: context.scaleType, rootKey: context.rootKey,
                          range: { min: 72, max: 96 }, // High register
                          random: this.random
                      });
                      arpEvents.forEach(e => { e.role = ABSTRACT_ROLES.HARMONY_COLOR; e.vel = 0.6; });
                      colorEvents.push(...arpEvents);
                 }
            }
            colorEvents.forEach(ev => ev.barIndex = barIndex);
            tracks[ABSTRACT_ROLES.HARMONY_COLOR].push(...colorEvents);
            addToRegistry(context.voiceRegistry, colorEvents);

            // -----------------------------------------------------------------
            // STEP 4: MELODY (Reads Registry)
            // -----------------------------------------------------------------
            let melodyStrategyName = traits ? (traits.melodyStrategy || 'lyrical') : 'lyrical';

            if (!traits.melodyStrategy) {
                if (traits.name && traits.name.includes('Baroque')) melodyStrategyName = 'counterpoint';
                if (traits.name === 'Horror / Tension' || traits.name === 'Avant-Garde') melodyStrategyName = 'swarm';
            }

            const isFantasy = traits.name && (traits.name.includes('Fantasy') || traits.name.includes('Romantic'));
            const isMinimalist = traits.name === 'Minimalist';

            if (context.config && context.config.vectors) {
                const vec = context.config.vectors;
                if (vec.harmonicComplexity > 0.7 && vec.energy < 0.6) {
                     if (chord.section === 'INTRO') melodyStrategyName = 'fantasy_arpeggio';
                }
            } else {
                if (isFantasy && (chord.section === 'INTRO' || chord.section === 'BREAK')) {
                    melodyStrategyName = 'fantasy_arpeggio';
                }
            }

            if (isMinimalist) melodyStrategyName = 'phasing';

            const isClimax = (chord.section === 'CLIMAX' || chord.section === 'DROP' || chord.section === 'SOLO');
            const allowSolo = !isMinimalist && traits.name && !traits.name.includes('Ambient') && !traits.name.includes('Lofi');

            if (isClimax && allowSolo) {
                 const isVigorous = traits.name.includes('Rock') || traits.name.includes('Jazz') || traits.name.includes('Metal') || traits.name.includes('Action') || traits.name.includes('Epic');
                 if (isVigorous) {
                      melodyStrategyName = 'virtuoso_solo';
                 }
            }

            const generator = getMelodyStrategy(melodyStrategyName);
            let currentPattern = sectionMotifMap[chord.section] || globalPattern;

            if (this.motifManager) {
                const sec = chord.section;
                let sectionMotifEvents = this.motifManager.getStoredMotif(sec);

                if (!sectionMotifEvents) {
                    let seed = null;
                    let strategy = 'identity';

                    if (sec.includes('CHORUS') || sec.includes('CLIMAX') || sec.includes('DROP')) {
                        seed = this.motifManager.getStoredMotif('VERSE') ||
                               this.motifManager.getStoredMotif('BUILD') ||
                               this.motifManager.getStoredMotif('INTRO');
                        strategy = 'heroic';
                    } else if (sec.includes('OUTRO')) {
                        seed = this.motifManager.getStoredMotif('CHORUS') ||
                               this.motifManager.getStoredMotif('CLIMAX') ||
                               this.motifManager.getStoredMotif('INTRO');
                        strategy = 'diminution';
                    } else if (sec.includes('BRIDGE')) {
                        seed = this.motifManager.getStoredMotif('VERSE') ||
                               this.motifManager.getStoredMotif('INTRO');
                        strategy = 'retrograde';
                    } else if (sec.includes('VERSE_B')) {
                         seed = this.motifManager.getStoredMotif('VERSE');
                         strategy = 'variation';
                    } else if (sec.includes('BUILD') || sec.includes('RISE')) {
                         seed = this.motifManager.getStoredMotif('VERSE') ||
                                this.motifManager.getStoredMotif('INTRO');
                         strategy = 'variation';
                    }

                    if (traits.name.includes('Action')) strategy = 'agitated';

                    sectionMotifEvents = this.motifManager.getVariation(strategy, sec, seed);

                    if (sectionMotifEvents && sectionMotifEvents.length > 0) {
                        this.motifManager.archiveMotif(sec, sectionMotifEvents);
                    }
                }

                if (sectionMotifEvents && sectionMotifEvents.length > 0) {
                    currentPattern = sectionMotifEvents.map(e => ({s: e.s, l: e.len, scaleDegree: e.scaleDegree}));
                }

                if (chord.isFill) {
                    const varRoll = this.random.next();
                    if (varRoll > 0.6) {
                         currentPattern = transformPattern(currentPattern, 'densify');
                    } else if (varRoll > 0.3) {
                         currentPattern = transformPattern(currentPattern, 'retrograde');
                    }
                }
            }
            else {
                if (barIndex % 4 === 2) currentPattern = transformPattern(currentPattern, 'inversion');
                if (barIndex % 4 === 3) currentPattern = transformPattern(currentPattern, 'retrograde');
            }

            // Removed forced augmentation to improve Motivic Repetition scores
            // if (chord.section && (chord.section.includes('CLIMAX') || chord.section.includes('DROP')) && (traits.name.includes('Epic') || traits.name.includes('Cinematic'))) {
            //    currentPattern = augmentRhythm(currentPattern, 2);
            // }

            const lastEv = tracks[ABSTRACT_ROLES.MELODY][tracks[ABSTRACT_ROLES.MELODY].length - 1];
            const prevNote = lastEv ? lastEv.d : (initialState.lastMelodyNote || null);
            const phraseType = (Math.floor(barIndex) % 2 === 0) ? 'question' : 'answer';

            // Operation Jailbreak: Resolve Tessitura for Melody
            // Get the primary instrument assigned to Melody
            let melodyTessitura = null;
            if (allocationMap[ABSTRACT_ROLES.MELODY] && allocationMap[ABSTRACT_ROLES.MELODY].length > 0) {
                const inst = allocationMap[ABSTRACT_ROLES.MELODY][0].instrument;
                if (inst && inst.tessitura) {
                    melodyTessitura = inst.tessitura;
                }
            }

            let melodyEvents = generator(chord, currentPattern, prevNote, {
                scaleType: chord.scale || context.scaleType,
                tessitura: melodyTessitura, // Inject Tessitura
                rootKey: chord.key || context.rootKey,
                barStart: barStart,
                barIndex: barIndex,
                phraseType: phraseType,
                strictMode: (profile.userScale && profile.userScale !== 'Auto') || (traits && traits.name && traits.name.includes('Baroque')),
                isImpressionist: (traits && (traits.name === 'Impressionist' || traits.name === 'Ethereal')),
                traits: traits, // Inject full traits for data-driven rules
                intervalProfile: traits ? traits.intervalProfile : undefined,
                melodicContour: traits ? traits.melodicContour : undefined,
                ornamentationStyle: traits ? traits.ornamentationStyle : undefined,
                motifManager: this.motifManager,
                voiceRegistry: context.voiceRegistry, // Inject Registry
                config: context.strategyConfig, // Inject Config
                // Task 1: Context Injection
                nextChord: expandedChords[chordIndex + 1],
                cache: context.cache,
                entropyState: initialState.entropyState, // Fix: Inject Persistent Entropy State from initialState
                random: this.random
            });

            if (this.motifManager) {
                melodyEvents = this.motifManager.adaptMotifToHarmony(melodyEvents, chord);
            }

            if (patternSet && patternSet.kick) {
                 melodyEvents = applyGrooveLocking(melodyEvents, { kickPattern: patternSet.kick, barStart: barStart });
            }

            melodyEvents.forEach(ev => ev.barIndex = barIndex);
            tracks[ABSTRACT_ROLES.MELODY].push(...melodyEvents);
            addToRegistry(context.voiceRegistry, melodyEvents);

            // Counter-Melody
            if (context.ensembleKey === 'orchestra' && (Math.floor(barIndex) % 4 === 3)) {
                 const runEvents = generateWoodwindRun(chord, barStart, context.scale, this.random);
                 runEvents.forEach(ev => ev.barIndex = barIndex);
                 tracks[ABSTRACT_ROLES.COUNTER_MELODY].push(...runEvents);
                 addToRegistry(context.voiceRegistry, runEvents);
            }

            const normalizedMelody = melodyEvents.map(e => ({...e, s: e.s - barStart}));
            const melodyAnalysis = analyzeMelody(normalizedMelody, 1);

            if (melodyStrategyName === 'counterpoint' || (!isMinimalist && !isFantasy)) {
                 const counterInst = allocationMap[ABSTRACT_ROLES.COUNTER_MELODY] && allocationMap[ABSTRACT_ROLES.COUNTER_MELODY][0]
                     ? allocationMap[ABSTRACT_ROLES.COUNTER_MELODY][0].instrument
                     : null;

                 const counterEvents = generateCounterLine(melodyEvents, [chord], context.scale, {
                    barStart: barStart,
                    prevNote: (tracks[ABSTRACT_ROLES.COUNTER_MELODY].length > 0) ? tracks[ABSTRACT_ROLES.COUNTER_MELODY][tracks[ABSTRACT_ROLES.COUNTER_MELODY].length-1].d : (initialState.lastCounterNote || null),
                    instrument: counterInst,
                    analysis: melodyAnalysis,
                    voiceRegistry: context.voiceRegistry
                 });
                 counterEvents.forEach(ev => ev.barIndex = barIndex);
                 tracks[ABSTRACT_ROLES.COUNTER_MELODY].push(...counterEvents);
                 addToRegistry(context.voiceRegistry, counterEvents);
            }
            else if (isMinimalist) {
                 const additiveGen = getMelodyStrategy('additive');
                 const addEvents = additiveGen(chord, null, null, {
                    scaleType: context.scaleType,
                    rootKey: context.rootKey,
                    barStart: barStart,
                    barIndex: barIndex
                 });
                 addEvents.forEach(ev => ev.barIndex = barIndex);
                 tracks[ABSTRACT_ROLES.COUNTER_MELODY].push(...addEvents);
                 addToRegistry(context.voiceRegistry, addEvents);
            }
            else if (traits.name === 'Romantic') {
                 const counterGen = getMelodyStrategy('lyrical');
                 const tenorEvents = counterGen(chord, currentPattern, null, {
                    scaleType: context.scaleType,
                    rootKey: context.rootKey,
                    barStart: barStart,
                    barIndex: barIndex,
                    range: { min: 43, max: 60 }
                 });
                 tenorEvents.forEach(ev => ev.barIndex = barIndex);
                 tracks[ABSTRACT_ROLES.COUNTER_MELODY].push(...tenorEvents);
                 addToRegistry(context.voiceRegistry, tenorEvents);
            }
        });

        // Task 1: Organic Lung (Stamina System)
        // Apply stamina logic post-generation to all tracks
        // Iterate time-sequentially to simulate resource usage
        Object.keys(tracks).forEach(role => {
            if (!tracks[role] || tracks[role].length === 0) return;

            // Sort by start time
            tracks[role].sort((a,b) => a.s - b.s);

            const tensionAtStart = context.getTension ? context.getTension(0) : 0.5; // Approx
            // Or sample tension per event

            const filtered = [];
            tracks[role].forEach(ev => {
                const barIdx = Math.floor(ev.s / (context.timeSig || 4));
                const tension = context.getTension ? context.getTension(barIdx) : tensionAtStart;

                // Check Stamina
                const canPlay = checkStamina(role, tension);

                if (!canPlay) {
                    // Resting... Recover budget
                    // Assume we rest for the duration of this would-be event (or until budget > threshold)
                    // Simple logic: If exhausted, rest for 4 beats (1 bar) then reset.
                    // Implementation: If budget <= 0, add 4 to budget (instant reset after rest decision?)
                    // No, that would play immediately next note.
                    // We need state: isResting.

                    // State stored in cache: context.cache.isResting[role] = endTime
                    if (!context.cache.restTimers) context.cache.restTimers = {};

                    const restEnd = context.cache.restTimers[role] || 0;

                    if (ev.s >= restEnd) {
                        // Start Rest
                        const restDuration = 4.0; // Rest for 1 bar
                        context.cache.restTimers[role] = ev.s + restDuration;
                        // Reset Budget after rest
                        // Note: This logic is tricky in a loop.
                        // We set a timer. Future events < timer are skipped.
                        // Once we pass timer, we reset budget.
                    }

                    // Skip event (Resting)
                    // But if we just finished resting?
                    if (ev.s >= (context.cache.restTimers[role] || 0)) {
                         // Reset Budget
                         // We are past the rest period.
                         // Delete rest timer?
                         delete context.cache.restTimers[role];
                         // Re-init budget
                         context.cache.breathBudgets[role] = undefined; // Force re-init next call
                         // Allowed to play now? Yes.
                         // But we just failed checkStamina.
                         // So we skip THIS event, and next one might pass.
                    }
                } else {
                    // Play
                    filtered.push(ev);
                    // Deduct cost
                    updateBreathBudget(role, ev.len || 0.25, tension);
                }
            });
            tracks[role] = filtered;
        });

        return tracks;
    }
}
